import { useState, useCallback } from 'react'
import { secureFetch } from '@/utils/SecureFetch'

export interface AIParsedAssignment {
	target: string
	targetType: 'absolute_role' | 'relative_selector'
	routeType: string
	routeDetails?: string
	confidence: number
}

export interface AIParsedConcept {
	name: string
	category: 'route' | 'blocking' | 'motion' | 'protection'
	targetingMode: 'absolute_role' | 'relative_selector'
	defaultSide: string | null
	assignments: AIParsedAssignment[]
	confidence: number
	ambiguities: string[]
}

export interface UseConceptAIParserReturn {
	description: string
	setDescription: (value: string) => void
	parsedConcept: AIParsedConcept | null
	isParsing: boolean
	error: string | null
	parse: () => Promise<void>
	clear: () => void
}

export function useConceptAIParser(playbookId: string): UseConceptAIParserReturn {
	const [description, setDescription] = useState('')
	const [parsedConcept, setParsedConcept] = useState<AIParsedConcept | null>(null)
	const [isParsing, setIsParsing] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const parse = useCallback(async () => {
		if (!description.trim() || description.trim().length < 5) {
			setError('Please provide a more detailed description')
			return
		}

		setIsParsing(true)
		setError(null)
		setParsedConcept(null)

		try {
			const response = await secureFetch(
				`/api/playbooks/${playbookId}/ai/parse-concept`,
				{
					method: 'POST',
					body: JSON.stringify({ description: description.trim() }),
				},
			)

			if (!response.ok) {
				const data = await response.json()
				setError(data.error ?? 'Failed to parse concept')
				return
			}

			const data = await response.json()
			setParsedConcept(data.concept)
		} catch {
			setError('Network error — please try again')
		} finally {
			setIsParsing(false)
		}
	}, [description, playbookId])

	const clear = useCallback(() => {
		setDescription('')
		setParsedConcept(null)
		setError(null)
	}, [])

	return { description, setDescription, parsedConcept, isParsing, error, parse, clear }
}
